/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useMemo, useRef, useEffect} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {authAPI} from '../../../api'
import {getUesr, useAuth} from '../../../context/auth'
import toastr from 'toastr';
import { useIntl } from 'react-intl'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { MenuComponent } from '../../../../_metronic/assets/ts/components'

import { useGoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';

const APPLE_CLIENT_ID = 'qa.fanar.chat'
const APPLE_SIGN_IN_REDIRECT_URI = window.env?.REACT_APP_APPLE_SIGN_IN_REDIRECT_URI && window.env?.REACT_APP_APPLE_SIGN_IN_REDIRECT_URI != '' ? window.env?.REACT_APP_APPLE_SIGN_IN_REDIRECT_URI : "https://chat.fanar.qa/api/auth/sign-in/apple"


const initialValues = {
  email: '',
  verification_code: '',
  password: ''
}

interface UserStatus {
  account: boolean;
  password: boolean;
}

enum OriginallyFrom {
  PASSWORD = 'password',
  GOOGLE = 'google',
  APPLE = 'apple'
}

enum SignInStep {
  EMAIL = 'email',
  VERIFICATION_CODE = 'verification_code',
  VERIFICATION_CODE_FOR_RESET_PASSWORD = 'verification_code_for_reset_password',
  PASSWORD = 'password',
  SET_PASSWORD = 'set_password',
  RESET_PASSWORD = 'reset_password'
}

enum VerificationPurpose {
  SIGN_IN = 'sign_in',
  SIGN_UP = 'sign_up',
  RESET_PASSWORD = 'reset_password'
}

export function SignIn() {

  const location = useLocation();
  const state = location.state as { accountDelete?: boolean };
  const { idToken } = useParams<{ idToken: string}>();

  useEffect(() => {
    if( idToken ){
      signIn('', '', idToken, OriginallyFrom.APPLE)
    }
  }, [idToken])

  useEffect(() => {
    if (state?.accountDelete) {
      toastr.info(intl.formatMessage({id: `user.delete_account.sign_in_required`}))
    }
  }, [state])

  const emailInput = useRef<HTMLInputElement | null>(null)
  const verificationCodeInput = useRef<HTMLInputElement | null>(null)
  const passwordInput = useRef<HTMLInputElement | null>(null)
  const [currentStep, setCurrentStep] = useState<SignInStep>(SignInStep.EMAIL)
  const [handling, setHandling] = useState<OriginallyFrom | null>(null)
  const [secret, setSecret] = useState<string>()
  const [accessTokenToSetPassword, setAccessTokenToSetPassword] = useState<string>()
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null)
  const {saveAuth, setCurrentUser, signout} = useAuth()
  const navigate = useNavigate()
  const intl = useIntl()
  const selectedLang = useLang()

  useEffect(() => {
    if (emailInput.current) {
      emailInput.current.focus()
    }
    MenuComponent.reinitialization()
    const hash = window.location.hash.replace('#', '')
    if (['expired','revoked'].includes(hash)) {
      toastr.info(intl.formatMessage({id: `user.session_${hash}`}))
      signout()
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  useEffect(() => {
    if (currentStep === SignInStep.VERIFICATION_CODE) {
      verificationCodeInput.current?.focus();
    } else if (currentStep === SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD ){
      userStatus && sendVerificaitonCode(userStatus, formik.values.email, false)
      verificationCodeInput.current?.focus();
    } else if ([SignInStep.PASSWORD, SignInStep.SET_PASSWORD, SignInStep.RESET_PASSWORD].includes(currentStep)) {
      passwordInput.current?.focus();
    }
  }, [currentStep])

  const signInSchema = useMemo(() => {
    if (currentStep === SignInStep.VERIFICATION_CODE) {
      return Yup.object().shape({
        verification_code: Yup.string()
          .required('Required')
      });
    } else if (currentStep === SignInStep.EMAIL) {
      return Yup.object().shape({
        email: Yup.string()
          .email('Wrong email format')
          .min(3, 'Minimum 3 symbols')
          .max(50, 'Maximum 50 symbols')
          .required('Required')
      });
    } else if (currentStep === SignInStep.PASSWORD) {
      return Yup.object().shape({
        password: Yup.string()
          .required(intl.formatMessage({id: `user.password_rule`}))
      });
    } else if ( [SignInStep.SET_PASSWORD, SignInStep.RESET_PASSWORD].includes(currentStep) ) {
      return Yup.object().shape({
        password: Yup.string()
          .min(8, intl.formatMessage({id: `user.password_rule`}, {n:8}))
          .max(50, intl.formatMessage({id: `user.password_rule`}, {n:50}))
          .required(intl.formatMessage({id: `user.password_rule`}))
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?/~\\|-]).*$/,
            intl.formatMessage({id: `user.password_rule`})
          ),
      });
    }
  }, [currentStep]);
  
  
  const formik = useFormik({
    initialValues,
    validationSchema: signInSchema,
    onSubmit: async (values) => {
      if ([SignInStep.PASSWORD, SignInStep.SET_PASSWORD, SignInStep.RESET_PASSWORD].includes(currentStep)) {
        if( currentStep !== SignInStep.PASSWORD ){
          await setPassword(values.email, values.password)
        }else{
          await signIn(values.email, values.password, '', OriginallyFrom.PASSWORD)
        }
      }else if ([SignInStep.VERIFICATION_CODE, SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD].includes(currentStep)) {
        verifyVerificationCode(values.email, values.verification_code)
      }else{
        setHandling(OriginallyFrom.PASSWORD)
        const {data: data} = await authAPI.getStatus(values.email)
        const userStatus: UserStatus = data['data']
        setUserStatus(userStatus)
        if( userStatus && (!userStatus.account || !userStatus.password) ){
          setCurrentStep(SignInStep.VERIFICATION_CODE)
          sendVerificaitonCode(userStatus, formik.values.email, false)
        }else{
          setCurrentStep(SignInStep.PASSWORD)
          setHandling(null)
        }
      }
    },
  })

  const setPassword = async (email:string, password:string) => {
    setHandling(OriginallyFrom.PASSWORD)
    if( accessTokenToSetPassword ){
      try{
        await authAPI.setPassword(accessTokenToSetPassword, password)
        await signIn(email, password, '', OriginallyFrom.PASSWORD)
      } catch(error: any) {
        const { status, config, data } = error.response
        if (status == 401){
          toastr.error(intl.formatMessage({id: `user.password_set_session_expired`}))
          setCurrentStep(SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD)
        }else{
          toastr.error(intl.formatMessage({id: `general.error`}))
        }
        setHandling(null)
      }
    }
  }

  const verifyVerificationCode = async (email:string, verification_code:string) => {
    if( secret ){
      setHandling(OriginallyFrom.PASSWORD)
      try{
        const {data: data} = await authAPI.verifyVerificationCode(email, secret, verification_code)
        setHandling(null)
        setAccessTokenToSetPassword(data['data']['access_token'])
        if( SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD === currentStep ){
          setCurrentStep(SignInStep.RESET_PASSWORD)
        }else{
          setCurrentStep(SignInStep.SET_PASSWORD)
        }
      } catch(error: any) {
        const { status, config, data } = error.response
        if (status == 400){
          const message = data['message'].toLowerCase()
          if (message.includes('expired')){
            toastr.info(intl.formatMessage({id: `user.verification_code_expired`}))
          }else{
            toastr.info(intl.formatMessage({id: `user.invalid_verification_code`}))
          }
        } else if (status == 429){
          toastr.error(intl.formatMessage({id: `user.verification_code_attempt_limit`}))
        } else {
          toastr.error(intl.formatMessage({id: `user.invalid_verification_code`}))
        }
      } finally {
        setHandling(null)
      }
    }else{
      toastr.error(intl.formatMessage({id: `user.no_verification_code`}))
    }
  }

  const signIn = async (email:string, password:string, access_token:string, originally_from:OriginallyFrom) => {
    setHandling(originally_from)
    try{
      const params = {
        email: email,
        password: password,
        access_token: access_token,
        originally_from: originally_from
      }
      await authAPI.signIn(params)
      const user = await getUesr()
      saveAuth({
        is_signed_in: true
      })
      setCurrentUser({
        email: user['email'],
        settings: user['settings']
      })
      if (state?.accountDelete) {
        navigate('/delete-account')
      }else{
        navigate('/c')
      }
    } catch(error: any) {
      const { status, config, data } = error.response
      if (status == 400){
        const message = data['message'].toLowerCase()
        if( message.includes('invalid')){
          toastr.info(intl.formatMessage({id: `user.invalid_email_password`}))
        }
      } else if (status == 429){
        toastr.error(intl.formatMessage({id: `user.password_attempt_limit`}, {minutes: Math.round(parseInt(data['data']['locked_for_seconds'])/60)}))
      }
    }finally{
      setHandling(null)
    }
  }

  const signInWithGoogle = useGoogleLogin({
    onSuccess: tokenResponse => signIn('', '', tokenResponse.access_token, OriginallyFrom.GOOGLE)
  });


  const sendVerificaitonCode = async (userStatus: UserStatus, email:string, isResending:boolean) => {
    setHandling(OriginallyFrom.PASSWORD)
    formik.setFieldValue('verificaiton_code', '')
    try{
      let {data: data} = await authAPI.sendVerificationCode(
        !userStatus?.account ? VerificationPurpose.SIGN_UP : VerificationPurpose.RESET_PASSWORD, 
        email, 
        selectedLang
      )
      if( data['message'] == 'backoff' ){
        toastr.info(intl.formatMessage({id: `user.verification_code_backoff`}, {seconds: parseInt(data['data']['backoff_in_seconds'])}))
      }else{
        toastr.info(isResending ? intl.formatMessage({id: `user.verification_code_resent`}): intl.formatMessage({id: `user.verification_code_sent`}))
        setSecret(data['data']['secret'])
        formik.setFieldValue('verification_code', '')
      }
    } catch(error: any) {
      const { status, config, data } = error.response
      if (status == 400){
        const message = data['message'].toLowerCase()
        if( message.includes('invalid')){
          toastr.info(intl.formatMessage({id: `user.invalid_verification_code`}))
        }else if (message.includes('expired')){
          toastr.info(intl.formatMessage({id: `user.verification_code_expired`}))
        }
      } else if (status == 429){
        toastr.error(intl.formatMessage({id: `user.verification_code_attempt_limit`}))
      }
    } finally {
      setHandling(null)
    }
  }

  const resendVerificationCode = () => {
    userStatus && sendVerificaitonCode(userStatus, formik.values.email, true)
  }

  const goBack = () => {
    setCurrentStep(SignInStep.EMAIL)
    formik.resetForm()
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 mw-350px fv-plugins-framework mt-md-0 mt-5'
      onSubmit={formik.handleSubmit}
      noValidate
    >
      {![SignInStep.EMAIL, SignInStep.PASSWORD].includes(currentStep)  && 
      <div className='text-center mb-5'>
        <h1 className='text-dark pb-0'>
          {
            currentStep === SignInStep.VERIFICATION_CODE 
            ?
            intl.formatMessage({id: `user.enter_verification_code`})
            :
            intl.formatMessage({id: `user.${currentStep}`})
          }</h1>
      </div>
      }

      <div className={`fv-row mb-5 form-floating ${[SignInStep.SET_PASSWORD, SignInStep.RESET_PASSWORD].includes(currentStep ) && 'd-none'}`}>
        <input
          placeholder={intl.formatMessage({id: `user.email`})}
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent h-60px border border-gray-400',
          )}
          type='email'
          name='email'
          autoComplete='off'
          readOnly={currentStep !== SignInStep.EMAIL}
          ref={emailInput}
        />
        <label className='form-label fs-6 text-gray-600'>{intl.formatMessage({id: `user.email`})}</label>
      </div>

      { [SignInStep.PASSWORD, SignInStep.SET_PASSWORD, SignInStep.RESET_PASSWORD].includes(currentStep) &&
      <div className={`${currentStep !== SignInStep.PASSWORD && "mb-5"}`}>
        <div className='fv-row form-floating'>
          <input
            ref={passwordInput}
            placeholder={intl.formatMessage({id: `user.password`})}
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent h-60px border border-gray-400'
            )}
            type='password'
            name='password'
          />
          <label className='form-label fs-6 text-gray-600'>{intl.formatMessage({id: `user.password`})}</label>
        </div>
        { currentStep !== SignInStep.PASSWORD && formik.touched.password && formik.errors.password &&
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>
              {intl.formatMessage({id: `user.password_rule`})}
              </span>
            </div>
          </div>
        }
        { currentStep === SignInStep.PASSWORD && 
          <div className="d-flex justify-content-end fs-base fw-semibold">
            <button type="button" className="btn btn-link link-primary" onClick={()=>setCurrentStep(SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD)}>{intl.formatMessage({id: `user.forgot_password`})}</button>
          </div>
        }
      </div>
      }
        
      { [SignInStep.VERIFICATION_CODE, SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD].includes(currentStep) && (
        <>
          <div className='fv-row form-floating'>
            <input
              placeholder={intl.formatMessage({id: `user.verification_code`})}
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('verification_code')}
              className={clsx(
                'form-control bg-transparent h-60px border border-gray-400'
              )}
              ref={verificationCodeInput}
              disabled={handling !== null}
            />
            <label className='form-label fs-6 text-gray-600'>{intl.formatMessage({id: `user.verification_code`})}</label>
          </div>
        </>
      )}

      { [SignInStep.VERIFICATION_CODE, SignInStep.VERIFICATION_CODE_FOR_RESET_PASSWORD].includes(currentStep) &&
        <div className="d-flex justify-content-end gap-3 fs-base fw-semibold">
            <button type="button" disabled={handling !== null} className="btn btn-link link-primary" onClick={resendVerificationCode}>{intl.formatMessage({id: `user.resend_verification_code`})}</button>
        </div>
      }

      <div className='d-grid pb-lg-0'>
        <button
          type='submit'
          className='btn btn-primary px-8 py-5 fs-4 w-100'
          disabled={handling !== null}
        >
          {(handling !== OriginallyFrom.PASSWORD) && <span className='indicator-label'>{intl.formatMessage({id: `general.continue`})}</span>}
          {handling === OriginallyFrom.PASSWORD && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {intl.formatMessage({id: `general.please_wait`})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        { currentStep !== SignInStep.EMAIL && (
          <button type="button" className="btn btn-link px-8 py-5 mt-2 fs-4 w-100" onClick={goBack}>{intl.formatMessage({id: `general.go_back`})}</button>
        )}
      </div>

      { currentStep === SignInStep.EMAIL &&
        <>
          <div className="separator separator-content my-14">
            <span className="w-125px text-gray-500 fw-semibold fs-7">Or</span>
          </div>
          <div className="mb-20 h-50px">
            <button 
              type="button" 
              className="btn btn-lg btn-google-sign-in px-8 py-5 w-100 fs-4 d-flex flex-center" 
              style={{border: 1, stroke:"#747775"}} 
              onClick={()=>signInWithGoogle()}
              disabled={handling !== null}
            >
              <img src="/images/logos/google-logo.svg" className="me-2 h-20px"/> 
              {(handling !== OriginallyFrom.GOOGLE) && <>{intl.formatMessage({id: 'user.continue_with_google'})}</>}
              {handling === OriginallyFrom.GOOGLE && (
                <span className='indicator-progress d-inline' style={{display: 'block'}}>
                  {intl.formatMessage({id: `general.please_wait`})}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            <AppleSignin
              authOptions={{
                clientId: APPLE_CLIENT_ID,
                scope: 'email name',
                redirectURI: APPLE_SIGN_IN_REDIRECT_URI,
                state: 'state',
                nonce: 'nonce',
                usePopup: false
              }}
              uiType="dark"
              onSuccess={(response) => console.info(response)}
              onError={(error) => console.error(error)}
              render={(props) => 
                <button {...props} disabled={handling !== null} className="btn btn-lg btn-google-sign-in px-8 py-5 w-100 fs-4 d-flex flex-center mt-4" >
                  <img src="/images/logos/apple-logo.svg" className="h-20px mx-n1" style={{ objectFit: 'cover', width: '40px' }} /> 
                  {(handling !== OriginallyFrom.APPLE) && <>{intl.formatMessage({id: 'user.continue_with_apple'})}</>}
                  {handling === OriginallyFrom.APPLE && (
                    <span className='indicator-progress d-inline' style={{display: 'block'}}>
                      {intl.formatMessage({id: `general.please_wait`})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              }
            />
          </div>
        </>
      }
    </form>
  )
}
